import { UseFormSetValue } from "react-hook-form";

import { employmentStatusEnumSchema } from "@libs/api/patients";
import {
  EXPENSE_NUMERIC_FIELDS,
  HouseholdMemberAddressFormType,
  HouseholdMemberFormType,
  HouseholdMemberType,
  INCOME_NUMERIC_FIELDS,
} from "@/domain/householdMember";

import { convertStringToNumber } from "./string";

export const cleanHouseholdMemberFormData = (
  formData: HouseholdMemberFormType,
): HouseholdMemberFormType => {
  const cleanedData = { ...formData };

  if (formData.hasNoMailingAddress) {
    cleanedData.mailingAddressStreetOne = null;
    cleanedData.mailingAddressStreetTwo = null;
    cleanedData.mailingAddressComplement = null;
    cleanedData.mailingAddressCity = null;
    cleanedData.mailingAddressState = null;
    cleanedData.mailingAddressZip = null;
  } else if (
    !formData.isHomeless &&
    !formData.shouldUseDifferentMailingAddress
  ) {
    // If there's valid address in the main address, we can copy it over to mailing address
    if (formData.mainAddressStreetOne) {
      cleanedData.mailingAddressStreetOne = formData.mainAddressStreetOne;
      cleanedData.mailingAddressStreetTwo = formData.mainAddressStreetTwo;
      cleanedData.mailingAddressComplement = formData.mainAddressComplement;
      cleanedData.mailingAddressCity = formData.mainAddressCity;
      cleanedData.mailingAddressState = formData.mainAddressState;
      cleanedData.mailingAddressZip = formData.mainAddressZip;
    } else {
      cleanedData.mailingAddressStreetOne = null;
      cleanedData.mailingAddressStreetTwo = null;
      cleanedData.mailingAddressComplement = null;
      cleanedData.mailingAddressCity = null;
      cleanedData.mailingAddressState = null;
      cleanedData.mailingAddressZip = null;
    }
  }

  if (formData.isHomeless) {
    cleanedData.mainAddressStreetOne = null;
    cleanedData.mainAddressStreetTwo = null;
    cleanedData.mainAddressComplement = null;
    cleanedData.mainAddressCity = null;
    cleanedData.mainAddressState = null;
    cleanedData.mainAddressZip = null;
  }

  if (cleanedData.medicaidSubscriberId === "") {
    cleanedData.medicaidSubscriberId = null;
  }

  if (cleanedData.medicaidCaseNumber === "") {
    cleanedData.medicaidCaseNumber = null;
  }

  if (cleanedData.email === "") {
    cleanedData.email = null;
  }

  return cleanedData;
};

type SetAddressFormFieldsParamsType = {
  setValue: UseFormSetValue<HouseholdMemberAddressFormType>;
  fields: Partial<HouseholdMemberType> | null;
  formattedAddress?: string;
};

export const setMainAddressFormFields = ({
  setValue,
  fields,
  formattedAddress,
}: SetAddressFormFieldsParamsType) => {
  setValue("mainAddressStreetOne", fields?.mainAddressStreetOne ?? null);
  setValue("mainAddressStreetTwo", fields?.mainAddressStreetTwo ?? null);
  setValue("mainAddressComplement", fields?.mainAddressComplement ?? null);
  setValue("mainAddressCity", fields?.mainAddressCity ?? null);
  setValue("mainAddressState", fields?.mainAddressState ?? null);
  setValue("mainAddressZip", fields?.mainAddressZip ?? null);
  setValue("mainCounty", fields?.mainCounty ?? null);
  setValue("mainFormattedAddress", formattedAddress ?? "");
};

export const setMailingAddressFormFields = ({
  setValue,
  fields,
  formattedAddress,
}: SetAddressFormFieldsParamsType) => {
  setValue("mailingAddressStreetOne", fields?.mailingAddressStreetOne ?? null);
  setValue("mailingAddressStreetTwo", fields?.mailingAddressStreetTwo ?? null);
  setValue(
    "mailingAddressComplement",
    fields?.mailingAddressComplement ?? null,
  );
  setValue("mailingAddressCity", fields?.mailingAddressCity ?? null);
  setValue("mailingAddressState", fields?.mailingAddressState ?? null);
  setValue("mailingAddressZip", fields?.mailingAddressZip ?? null);
  setValue("mailingCounty", fields?.mailingCounty ?? null);
  setValue("mailingFormattedAddress", formattedAddress ?? "");
};

export const formatHouseholdMemberName = (
  householdMember: Pick<HouseholdMemberType, "firstName" | "lastName">,
  formatType:
    | "fullFirstLastInitial"
    | "fullName"
    | "firstNameOnly" = "fullFirstLastInitial",
) => {
  switch (formatType) {
    case "fullName":
      return householdMember.firstName && householdMember.lastName
        ? `${householdMember.firstName} ${householdMember.lastName}`
        : (householdMember.firstName ?? "");
    case "fullFirstLastInitial":
      return householdMember.lastName?.[0]
        ? `${householdMember.firstName} ${householdMember.lastName[0].toUpperCase()}.`
        : (householdMember.firstName ?? "");
    case "firstNameOnly":
      return householdMember.firstName ?? "";
  }
};

/**
 * True when the member has any income amount, or has completed the unemployed
 * flow (employment status + unemployment compensation question) without necessarily
 * entering a dollar amount.
 */
export const hasIncome = (member: HouseholdMemberType): boolean => {
  if (
    member.employmentStatus === employmentStatusEnumSchema.enum.UNEMPLOYED &&
    member.unemploymentCompensationStatus != null
  ) {
    return true;
  }
  return INCOME_NUMERIC_FIELDS.some(field => member[field] != null);
};

/**
 * Checks if a household member has any income recorded or if they have set no income.
 */
export const hasIncomeConfig = (member: HouseholdMemberType): boolean => {
  return hasIncome(member) || member.hasIncome === false;
};

export const transformFormValues = (
  formValues: Record<string, unknown>,
  numericFieldsNames: readonly string[],
): Record<string, unknown> => {
  const transformedValues = { ...formValues };
  for (const field of numericFieldsNames) {
    if (field in formValues) {
      transformedValues[field] = convertStringToNumber(
        transformedValues[field] as string | null | undefined,
      );
    }
  }
  return transformedValues;
};

export const hasExpenses = (member: HouseholdMemberType): boolean => {
  return (
    EXPENSE_NUMERIC_FIELDS.some(field => member[field] != null) ||
    member.medicalExpenses3MonthsHelpNeededStatus != null
  );
};

export const hasExpensesConfig = (member: HouseholdMemberType): boolean => {
  return hasExpenses(member) || member.hasExpense === false;
};

export const getAllHouseholdMembersNames = (
  householdMembers?: HouseholdMemberType[],
  formatType:
    | "fullFirstLastInitial"
    | "fullName"
    | "firstNameOnly" = "fullFirstLastInitial",
): string[] => {
  return (
    householdMembers?.map(member =>
      formatHouseholdMemberName(member, formatType),
    ) ?? []
  );
};
